function ParkChicago() {
    return (
        <div className="project-page">
            {/* Project Header */}
            <h1 className="github-link">
                Specialty Coffee: Packaging Strategy
            </h1>

            {/* Overview Section */}
            <div className="tech-tools">
                <div className="project-duration">
                    <p><strong>Duration:</strong> 5 months </p>
                    <p><strong>Role:</strong> Product Experience Strategist</p>
                    <p><strong>Team Members:</strong> 3</p>
                    <p>
                        <strong>My contribution:</strong> Bridged the gap between technical constraints and business goals by leading user research and market-fit analysis to strategize a packaging system that reduced operational costs by 15%.
                    </p>
                </div>
            </div>

            <h2>Product Exploration</h2>
            <p>We executed a <strong>mixed-methods research study</strong> to triangulate pain points across the specialty coffee ecosystem. This included <strong>semi-structured interviews</strong> with roasters, <strong>ethnographic observation</strong> of consumer brewing rituals, and a <strong>thematic analysis</strong> of reviews and competitor packaging architectures.</p>
            <ul>
                <li><strong>"It arrived stale":</strong> The Peak Freshness Gap.</li>
                <span className="img-descript">↑ Negative reviews frequently cited <strong>bag bloating</strong> or lack of a <strong>one-way degassing valve</strong>, which directly impacted flavor.</span>
                <li><strong>"Form Over Function":</strong> The Reseal Frustration.</li>
                <span className="img-descript">↑ Many brands used premium exteriors but failed to provide a <strong>resealable inner bag</strong>, forcing users to use binder clips for storage.</span>
                <li><strong>"Too Much Plastic":</strong> The Sustainability Critique.</li>
                <span className="img-descript">↑ Users explicitly called out <strong>non-recyclable multi-layer foils</strong>; waste is heavily frowned upon in the specialty community.</span>
                <li><strong>"Who Grew This?":</strong> The Missing Narrative.</li>
                <span className="img-descript">↑ A recurring complaint was the lack of <strong>traceability data</strong>—users felt the single-origin aspect was lost without farmer and altitude details.</span>
            </ul>


            <h2>Product Design Requirements</h2>
            <p>We translated research insights into 8 core requirements, prioritizing <strong>Functional Integrity</strong> and <strong>Operational Efficiency.</strong></p>
            <div className="requirements-grid">
                <ul>
                    <li><strong>Information Hierarchy:</strong> Keep it simple. Packaging must be highly informative regarding origin, but we will <strong>eliminate marketing fluff</strong> to reduce cognitive load.</li>
                    <li><strong>Atmospheric Shielding:</strong> Prioritize bean preservation. All secondary containers must be <strong>UV-shielded</strong>, oxygen-purging, and feature airtight silicone gaskets.</li>
                    <li><strong>Sustainable Circularity:</strong> Move away from multi-layer foils toward <strong>mono-material recyclables</strong> or reusable "Anchor" vessels to minimize environmental impact.</li>
                    <li><strong>Physical Validation:</strong> Transformed passive "Best By" dates into <strong>prominent Roast Date tracking</strong> to provide users with actionable freshness verification.</li>
                    <li><strong>Frictionless Access:</strong> Standardized <strong>integrated reseal mechanisms</strong> (integrated zippers or vacuum seals) to eliminate the need for external clips.</li>
                    <li><strong>Intuitive Iconography:</strong> Use high-contrast visual cues for <strong>brew methods and flavor profiles</strong> to help users navigate selection without dense text blocks.</li>
                </ul>
            </div>

            <h2>Constraints</h2>
            <p> Our client wanted cost efficiency while providing a premium feel, without sacrificing bean quality.
            </p>


            <h2>Key Improvements</h2>
            <div className="improvements-list">
                <ul>
                    <li><strong>Customizable Recents:</strong> Added names, icons, and "Favorite" pinning.</li>
                    <li><strong>Validation Events:</strong> Replaced the "Wall of Text" with a confirmation modal that appears only when needed.</li>
                    <li><strong>High-Contrast UI:</strong> Removed visual noise for 100% outdoor readability.</li>
                    <li><strong>Integrated Map:</strong> Embedded GPS-linked zone detection directly into the home screen.</li>
                </ul>
            </div>

            <p className="disclaimer">
                <strong>Note:</strong> While I led the research, audit, and strategic requirements, the final
                visual identity and UI iterations were executed by my design teammates. This is a course project.
            </p>
        </div>
    );
};

export default ParkChicago;